import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Layers3, Leaf, MessageSquareQuote, Palette, Sparkles, Star, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { FEATURED, POPULAR_CATEGORIES, WHAT_WE_MAKE } from "../data/siteContent";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";
import LaserEtchedPrice from "../components/LaserEtchedPrice";
import LaserEtchedLabel from "../components/LaserEtchedLabel";
import { variantAvailability, variantCanBePurchased } from "../lib/variantAvailability";

const heroImage = "/imported-images/Beyond%20Board%20Woodworking%20Website%20Pictures/IMG_20260920_210520.jpg";
const dollars = (minor) => `$${(Number(minor || 0) / 100).toFixed(2)}`;
const FEATURE_ROTATION_MS = 5000;
const MAX_FEATURED = 5;

const normalizeFeaturedItem = (item) => ({
  ...item,
  image: item.image || item.image_url,
  category: item.category || item.categories?.[0] || "Featured",
});

// Manual featured selections always win. The /featured endpoint already returns
// the saved manual picks (in saved order) plus any deterministic fallback it
// chose. We fill whatever slots remain (up to 5) from the gallery/static
// catalog, excluding anything already selected. Manual items are never
// reordered, duplicated, or replaced.
export const selectFeaturedItems = ({ manualFeatured = [], galleryItems = [], staticItems = [] }) => {
  const manual = manualFeatured.filter((item) => item && item.id).slice(0, MAX_FEATURED);
  const manualIds = new Set(manual.map((item) => item.id));
  const seen = new Set(manualIds);
  const candidates = [...galleryItems, ...staticItems].filter(
    (item) => item && item.id && !manualIds.has(item.id),
  );
  const fallback = [];
  for (const item of candidates) {
    if (fallback.length >= MAX_FEATURED - manual.length) break;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    fallback.push(item);
  }
  return [...manual, ...fallback].slice(0, MAX_FEATURED).map(normalizeFeaturedItem);
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [featuredReady, setFeaturedReady] = useState(false);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [hoveredWorkId, setHoveredWorkId] = useState(null);
  const [selectedFeaturedItem, setSelectedFeaturedItem] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);
  const [categoryGallery, setCategoryGallery] = useState([]);
  const [categoryIndexes, setCategoryIndexes] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const { addItem } = useCart();

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        // /featured returns a raw array (manual picks first, backend fallback
        // filling any empty slots) — not an { items } wrapper.
        const featuredResponse = await api.get("/featured");
        const manualFeatured = Array.isArray(featuredResponse.data)
          ? featuredResponse.data
          : (featuredResponse.data?.items || []);
        const galleryResponse = await api.get("/gallery").catch(() => ({ data: [] }));
        const galleryItems = Array.isArray(galleryResponse.data) ? galleryResponse.data : [];
        setFeatured(selectFeaturedItems({ manualFeatured, galleryItems, staticItems: FEATURED }));
        setFeaturedIndex(0);
      } catch {
        setFeatured(selectFeaturedItems({ staticItems: FEATURED }));
        setFeaturedIndex(0);
      } finally {
        setFeaturedReady(true);
      }
    };
    loadFeatured();
  }, []);

  useEffect(() => {
    api.get("/products").then((response) => {
      setShopProducts((response.data || []).filter((product) => !product.needs_pricing).slice(0, 6));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/gallery").then((response) => {
      setCategoryGallery(Array.isArray(response.data) ? response.data : []);
    }).catch(() => setCategoryGallery([]));
  }, []);

  const categorySlides = useMemo(() => POPULAR_CATEGORIES.map((category) => {
    const matchingImages = categoryGallery
      .filter((item) => (item.categories || [item.category]).includes(category.name))
      .map((item) => item.image || item.image_url)
      .filter(Boolean);
    return [...new Set([category.image, ...matchingImages])];
  }), [categoryGallery]);

  useEffect(() => {
    if (!categorySlides.some((slides) => slides.length > 1)) return undefined;
    const timer = window.setInterval(() => {
      setCategoryIndexes((current) => Object.fromEntries(categorySlides.map((slides, index) => [index, slides.length > 1 ? ((current[index] || 0) + 1) % slides.length : 0])));
    }, 4200);
    return () => window.clearInterval(timer);
  }, [categorySlides]);

  useEffect(() => {
    if (!featuredReady || featured.length < 2) return undefined;
    const advance = () => setFeaturedIndex((current) => (current + 1) % featured.length);
    const timer = window.setInterval(() => {
      if (!document.hidden) advance();
    }, FEATURE_ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [featured.length, featuredReady]);

  const featuredItem = featured[featuredIndex % featured.length];
  const featuredImage = featuredItem?.image || heroImage;
  const [heroImageState, setHeroImageState] = useState({ image: featuredImage, previous: null, incomingVisible: true });

  useEffect(() => {
    setHeroImageState((current) => {
      if (current.image === featuredImage) return current;
      return { image: featuredImage, previous: current.image, incomingVisible: false };
    });
    const frame = window.requestAnimationFrame(() => {
      setHeroImageState((current) => ({ ...current, incomingVisible: true }));
    });
    const cleanup = window.setTimeout(() => {
      setHeroImageState((current) => ({ ...current, previous: null }));
    }, 750);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(cleanup);
    };
  }, [featuredImage]);

  useEffect(() => {
    const nextImage = featured[(featuredIndex + 1) % featured.length]?.image;
    if (!nextImage || nextImage === featuredImage) return undefined;
    const preload = new window.Image();
    preload.src = nextImage;
    return undefined;
  }, [featured, featuredIndex, featuredImage]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || !featuredReady) return;
    console.table({
      href: window.location.href,
      origin: window.location.origin,
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      navbarEtched: window.__bbwNavbarEtched ? "true" : null,
      engravingPhase: window.__bbwNavbarEtched ? "complete" : "navbar",
      featuredProductId: featuredItem?.id || null,
      featuredProductTitle: featuredItem?.title || null,
      featuredIndex,
      productCount: featured.length,
      reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    });
  }, [featured, featuredIndex, featuredItem, featuredReady]);

  return (
    <div className="home-workshop min-h-screen" data-testid="home-page">
      <Seo
        title="Custom Laser-Cut Wood Signs & Decor"
        description="Shop custom laser-cut wood signs, personalized door decor, seasonal designs, and made-to-order wood pieces from Beyond Board Woodworking. Browse finished designs or start a custom order."
        path="/"
      />
      <section className="home-workshop-hero relative overflow-hidden border-b" data-testid="home-hero">
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {heroImageState.previous && (
              <img
                key={`background-previous-${heroImageState.previous}`}
                src={heroImageState.previous}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-[14px] transition-opacity duration-700 ease-in-out motion-reduce:transition-none"
                style={{ opacity: heroImageState.incomingVisible ? 0 : 0.38 }}
                onError={(event) => { event.currentTarget.style.opacity = "0"; }}
              />
            )}
            <img
              key={`background-${featuredItem?.id || heroImageState.image}`}
              src={heroImageState.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-[14px] transition-opacity duration-700 ease-in-out motion-reduce:transition-none"
              style={{ opacity: heroImageState.incomingVisible ? 0.38 : 0 }}
              onError={(event) => { event.currentTarget.style.opacity = "0"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#07131a]/80 via-[#07131a]/42 to-[#07131a]/18" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs uppercase tracking-[0.28em] text-[#26C6D5] font-semibold mb-6">
              Handcrafted · Layered · Personalized
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-[#F5F0E6] leading-[1.05] tracking-tight">Custom Laser-Cut Wood Signs Made Your Way</h1>
            <p className="mt-8 text-[#B8B7B2] max-w-3xl text-lg leading-relaxed">From personalized door signs and seasonal pieces to funny, patriotic, pet, sports, and completely custom designs, Beyond Board Woodworking creates wood decor made to stand out.</p>
            <p className="mt-4 text-[#B8B7B2] max-w-3xl text-lg leading-relaxed">Browse our finished designs for inspiration or bring us your own idea and we'll help turn it into something made for you.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/gallery">
                <Button data-laser-plaque className="home-wood-action home-hero-wood-cta bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md h-12 px-6 text-base shadow-sm" data-testid="home-cta-gallery">
                  <LaserEtchedLabel text="Browse the Gallery" kind="button" onceKey="home-cta-gallery" /> <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/quote">
                <Button data-laser-plaque variant="outline" className="home-wood-action home-hero-wood-cta border-[#26C6D5]/60 text-[#26C6D5] hover:bg-[#26C6D5]/10 hover:text-[#8FE8F8] rounded-md h-12 px-6 text-base bg-transparent" data-testid="home-cta-quote">
                  <LaserEtchedLabel text="Start a Custom Order" kind="button" onceKey="home-cta-quote" />
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-[#B8B7B2] text-sm flex-wrap">
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#D8A34B] fill-[#D8A34B]" /> Custom work for families, gifts, and small businesses</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#D8A34B]" /> Thoughtful quotes and hand-finished production</div>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block fade-up">
            <div data-laser-plaque className="home-featured-plaque group relative aspect-square rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedFeaturedItem(featuredItem)}>
                {heroImageState.previous && (
                  <img
                    key={`card-previous-${heroImageState.previous}`}
                    src={heroImageState.previous}
                    className={`absolute inset-0 w-full h-full object-contain p-5 transition-opacity duration-700 ease-in-out motion-reduce:transition-none ${heroImageState.incomingVisible ? "opacity-0" : "opacity-100"}`}
                    alt=""
                    aria-hidden="true"
                    onError={(event) => { event.currentTarget.style.opacity = "0"; }}
                  />
                )}
                <img
                  key={`card-${featuredItem?.id || heroImageState.image}`}
                  src={heroImageState.image}
                    className={`absolute inset-0 w-full h-full object-contain p-5 transition-opacity duration-700 ease-in-out motion-reduce:transition-none ${heroImageState.incomingVisible ? "opacity-100" : "opacity-0"}`}
                  alt="layered basswood sign"
                  onError={(event) => { event.currentTarget.style.opacity = "0"; }}
                />
              <div className="absolute inset-0 bg-gradient-to-t from-[#15191C]/90 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <LaserEtchedLabel text="Featured Piece" kind="plaque" onceKey="home-featured-label" className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-2" />
                <LaserEtchedLabel text={featuredItem?.title || "Featured Piece"} kind="plaque" onceKey={`home-featured-title-${featuredItem?.id || "default"}`} className="font-display text-2xl text-[#F5F0E6]" />
                <p className="text-[#B8B7B2] text-sm mt-2">{featuredItem?.description || "Layered basswood, painted accents, and handcrafted finishing."}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-featured">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#26C6D5] mb-3">Our Work</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6]">Portfolio highlights from recent custom pieces</h2>
          </div>
          <Link to="/gallery">
            <Button variant="ghost" className="home-wood-action text-[#26C6D5] hover:text-[#8FE8F8] hover:bg-[#26C6D5]/10">
              Browse the gallery <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((item, i) => (
            <article
              key={item.id}
              tabIndex={0}
              data-laser-plaque
              className="group home-work-card rounded-2xl overflow-hidden border border-[#F5F0E6]/10 bg-[#1d2428]/80 hover:border-[#26C6D5]/40 hover:shadow-[0_8px_30px_rgba(38,198,213,0.18)] hover:-translate-y-1 transition-all duration-300 fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              onMouseEnter={() => setHoveredWorkId(item.id)}
              onMouseMove={() => setHoveredWorkId(item.id)}
              onMouseLeave={() => setHoveredWorkId(null)}
              onFocus={() => setHoveredWorkId(item.id)}
              onBlur={() => setHoveredWorkId(null)}
              onClick={() => setHoveredWorkId(item.id)}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" onClick={() => setSelectedFeaturedItem(item)} />
                {hoveredWorkId === item.id && item.product && !item.product.needs_pricing && item.product.product_type !== "mason_jar" && <LaserEtchedPrice replayKey={item.id} className="customer-wood-price absolute bottom-3 left-3" segments={[{ text: dollars(item.product.sale_price_minor ?? item.product.price_minor), className: "text-lg font-semibold text-[#3a1d0f]" }]} />}
              </div>
              <div className="p-6">
                <LaserEtchedLabel text={item.category} kind="plaque" delay={i * 80} onceKey={`home-work-category-${item.id}`} className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-2" />
                <LaserEtchedLabel as="h3" text={item.title} kind="plaque" delay={i * 80} onceKey={`home-work-title-${item.id}`} className="font-display text-xl text-[#F5F0E6]" />
                <p className="text-[#B8B7B2] text-sm mt-2">{item.description}</p>
                <Link to="/quote" state={{ inspiration: item.title, category: item.category }}>
                  <Button variant="outline" className="home-wood-action home-work-similar-cta mt-5 bg-transparent border-[#26C6D5]/60 text-[#26C6D5] hover:bg-[#26C6D5]/10 hover:text-[#8FE8F8] w-full">
                    <LaserEtchedLabel text="Request Something Similar" kind="button" delay={i * 80} onceKey={`home-work-action-${item.id}`} /> <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {selectedFeaturedItem && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedFeaturedItem(null)}>
          <div className="relative w-full max-w-5xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-3xl border border-stone-100/10 bg-zinc-950 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setSelectedFeaturedItem(null)} className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900/80 text-stone-100 border border-stone-100/10" aria-label="Close featured piece details"><X className="w-5 h-5" /></button>
            <div className="grid md:grid-cols-[1.35fr_0.65fr]">
              <div className="bg-zinc-950"><img src={selectedFeaturedItem.image} alt={selectedFeaturedItem.title} className="w-full max-h-[72vh] object-contain" /></div>
              <div className="p-6 sm:p-8 bg-zinc-950/95">
                <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-3">Featured Piece</p>
                <h2 className="font-display text-4xl text-[#F5F0E6] leading-none">{selectedFeaturedItem.title}</h2>
                <p className="mt-4 text-[#B8B7B2] leading-relaxed">{selectedFeaturedItem.description}</p>
                {selectedFeaturedItem.product?.product_type === "mason_jar" ? <div className="mt-6 rounded-xl border border-stone-100/10 bg-zinc-900/70 p-4"><p className="text-xs uppercase tracking-[0.2em] text-stone-500">Available sizes</p>{(selectedFeaturedItem.product.variants || []).filter((variant) => variant.active !== false).map((variant) => <div key={variant.id} className="mt-2 flex justify-between gap-4 text-stone-200"><span>{variant.name}</span><span className="text-right"><span className="font-semibold text-[#D8A34B]">{dollars(variant.sale_price_minor ?? variant.price_minor)}</span><span className="ml-2 text-xs text-stone-400">{variantAvailability(variant).label}</span></span></div>)}</div> : selectedFeaturedItem.product?.needs_pricing ? <p className="mt-6 text-sm text-[#D8A34B]">Pricing is being finalized. Request a custom version for details.</p> : selectedFeaturedItem.product && <div className="mt-6 flex items-baseline gap-3">{selectedFeaturedItem.product.sale_price_minor != null && selectedFeaturedItem.product.regular_price_minor != null && selectedFeaturedItem.product.sale_price_minor < selectedFeaturedItem.product.regular_price_minor ? <><span className="text-lg text-stone-500 line-through">{dollars(selectedFeaturedItem.product.regular_price_minor)}</span><span className="text-3xl font-semibold text-[#D8A34B]">{dollars(selectedFeaturedItem.product.sale_price_minor)}</span></> : <span className="text-3xl font-semibold text-[#D8A34B]">{dollars(selectedFeaturedItem.product.price_minor)}</span>}</div>}
                <div className="mt-8 space-y-3"><Link to="/quote" state={{ inspiration: selectedFeaturedItem.title, category: selectedFeaturedItem.category }} onClick={() => setSelectedFeaturedItem(null)}><Button className="w-full bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md h-11">Request Something Similar <ArrowRight className="w-4 h-4 ml-2" /></Button></Link><Link to="/gallery" onClick={() => setSelectedFeaturedItem(null)}><Button variant="outline" className="w-full bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10 rounded-md h-11">View Gallery</Button></Link></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="home-workshop-categories py-24 border-y" data-testid="home-categories">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Popular Categories</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6] mb-12">The kinds of pieces people ask for most</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {POPULAR_CATEGORIES.map((cat, categoryIndex) => (
              <Link
                key={cat.name}
                to="/gallery"
                state={{ category: cat.name }}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#F5F0E6]/10 block"
              >
                <img src={categorySlides[categoryIndex][categoryIndexes[categoryIndex] || 0]} alt={cat.name} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-display text-xl text-[#F5F0E6]">{cat.name}</p>
                  <p className="text-xs text-[#D8A34B] mt-1 flex items-center gap-1">View examples <ArrowRight className="w-3 h-3" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-what-we-make-preview">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">What We Make</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6]">Custom & personalized wood decor for homes, gifts, and business spaces</h2>
            <p className="mt-4 text-[#B8B7B2] max-w-3xl leading-relaxed">
              Start with one of our existing designs or bring your own idea. Names, wording, themes, and other details can be personalized where the design allows. Many pieces are made to order — created after the order is placed rather than pulled from a warehouse of identical products.
            </p>
          </div>
          <Link to="/what-we-make">
            <Button variant="ghost" className="home-wood-action text-[#D8A34B] hover:text-[#F5F0E6] hover:bg-[#D8A34B]/10">
              See all categories <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {WHAT_WE_MAKE.slice(0, 6).map((item, i) => (
            <div key={item.id} className="p-6 rounded-2xl bg-zinc-900/80 border border-[#F5F0E6]/10 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <h3 className="font-display text-2xl text-[#F5F0E6]">{item.title}</h3>
              <p className="text-[#B8B7B2] text-sm mt-3 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
        {shopProducts.length > 0 && <div className="mt-12">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4"><div><p className="mb-2 text-xs uppercase tracking-[0.2em] text-[#26C6D5]">Ready to shop</p><h3 className="font-display text-3xl text-[#F5F0E6]">Available pieces from inventory</h3></div><Link to="/gallery"><Button variant="ghost" className="text-[#26C6D5]">View all pieces <ArrowRight className="ml-2 h-4 w-4" /></Button></Link></div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">{shopProducts.map((product) => {
            const selectedVariant = selectedVariants[product.id];
            const variant = product.variants?.find((item) => item.id === selectedVariant);
            const price = variant?.sale_price_minor ?? variant?.price_minor ?? product.sale_price_minor ?? product.price_minor ?? product.from_price_minor;
            const saleActive = product.sale_price_minor != null && product.regular_price_minor != null && product.sale_price_minor < product.regular_price_minor && !variant;
            const selectedAvailability = variant ? variantAvailability(variant) : null;
            return <article key={product.id} className="overflow-hidden rounded-2xl border border-[#F5F0E6]/10 bg-zinc-900/80">
              <div className="relative"><img src={product.image_url || "/branding/bbw-logo.jpg"} alt={product.name} className="h-44 w-full object-cover" />{!variant && price != null && <LaserEtchedPrice className="absolute bottom-3 left-3 rounded-md border border-[#D8A34B]/50 bg-zinc-950/90 px-3 py-2 shadow-lg" segments={saleActive ? [{ text: dollars(product.regular_price_minor), className: "mr-2 text-sm text-stone-400 line-through", animate: false }, { text: dollars(product.sale_price_minor), className: "text-lg font-semibold text-[#D8A34B]" }] : [{ text: `${product.product_type === "mason_jar" ? "From " : ""}${dollars(price)}`, className: "text-lg font-semibold text-[#F5F0E6]" }]} replayKey={product.id} observe />}</div>
              <div className="p-5"><h4 className="font-display text-xl text-[#F5F0E6]">{product.name}</h4>
                {price != null && <div className="mt-2 flex items-baseline gap-2">{saleActive ? <><span className="text-sm text-stone-500 line-through">{dollars(product.regular_price_minor)}</span><span className="text-xl font-semibold text-[#D8A34B]">{dollars(product.sale_price_minor)}</span></> : <span className="text-xl font-semibold text-[#D8A34B]">{product.product_type === "mason_jar" && !variant ? "From " : ""}{dollars(price)}</span>}</div>}
                {product.product_type === "mason_jar" && <select value={selectedVariant || ""} onChange={(event) => setSelectedVariants((current) => ({ ...current, [product.id]: event.target.value }))} className="mt-3 w-full rounded-md border border-stone-100/10 bg-zinc-950 px-2 py-2 text-sm text-stone-100"><option value="">Choose size</option>{(product.variants || []).filter((item) => item.active !== false).map((item) => { const availability = variantAvailability(item); return <option key={item.id} value={item.id} disabled={!availability.purchasable}>{item.name} - {dollars(item.sale_price_minor ?? item.price_minor)} · {availability.label}</option>; })}</select>}
                {selectedAvailability && <p className="mt-2 text-xs text-stone-400">{selectedAvailability.label}</p>}
                <Button type="button" disabled={product.product_type === "mason_jar" && (!variant || !variantCanBePurchased(variant))} onClick={() => addItem({ product_id: product.id, variant_id: selectedVariant || null })} className="mt-4 w-full bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2]">Add to Cart</Button>
              </div>
            </article>;
          })}</div>
        </div>}
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-why">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Why Beyond Board</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6] leading-tight">
              Basswood pieces with depth, warmth, and a handcrafted finish
            </h2>
            <p className="mt-6 text-[#B8B7B2] leading-relaxed">
              Our work is built around laser-cut basswood because it layers cleanly,
              stays lightweight, and creates a beautiful raised look for painted and
              stained custom decor. Each piece is reviewed, finished, and assembled by hand.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: Layers3, title: "Layered basswood depth", body: "Raised multi-piece detail for a dimensional 3D look." },
              { icon: Palette, title: "Painted and stained finishes", body: "Color, contrast, and hand-finished detail tailored to your style." },
              { icon: Leaf, title: "Lightweight wall-ready decor", body: "Basswood works beautifully for door hangers, nursery signs, and wall pieces." },
              { icon: MessageSquareQuote, title: "Quote-first custom process", body: "Send your idea and we will review the design direction, size, and production plan with you." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="p-6 rounded-2xl bg-zinc-900/80 border border-[#F5F0E6]/10 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="w-11 h-11 rounded-md bg-[#D8A34B]/15 border border-[#D8A34B]/40 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#D8A34B]" />
                  </div>
                  <h3 className="font-display text-xl text-[#F5F0E6]">{item.title}</h3>
                  <p className="text-[#B8B7B2] text-sm mt-2">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-900/50 border-y border-[#F5F0E6]/5" data-testid="home-custom-orders-callout">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Custom Orders</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6] leading-tight">
              Have an idea in mind? Send it over and we will help shape it into a finished piece.
            </h2>
            <p className="mt-6 text-[#B8B7B2] leading-relaxed max-w-3xl">
              Beyond Board covers everyday home decor, seasonal pieces, pets and animals, sports,
              patriotic designs, outdoor themes, nature, novelty pieces, and more. Start with one of
              our designs or send a rough idea, screenshot, logo, or inspiration photo, and we will
              guide you through what is possible.
            </p>
          </div>
          <div className="lg:col-span-5 rounded-3xl border border-[#F5F0E6]/10 bg-zinc-950/60 p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B] mb-3">A few good starting points</p>
            <ul className="space-y-3 text-[#B8B7B2]">
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-[#D8A34B] mt-1" /> Request a piece inspired by something in the gallery.</li>
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-[#D8A34B] mt-1" /> Send a rough idea, screenshot, logo, or inspiration photo.</li>
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-[#D8A34B] mt-1" /> Ask about larger custom sizes available by request.</li>
            </ul>
            <Link to="/quote">
              <Button className="home-wood-action mt-6 bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C] h-12 px-6 w-full">
                Request a Custom Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-[#B8B7B2]">
              Questions first? Read our <Link to="/faq" className="text-[#D8A34B] underline underline-offset-4 hover:text-[#F5F0E6]">frequently asked questions</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
